import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, forkJoin, map, mergeMap, of, tap } from 'rxjs';
import { UpdateStudyAndStudyFileStatusResponse } from 'src/app/models/status/status.model';
import { StudyService } from 'src/app/shared-new/services/study.service';
import { ActivityLogService } from '../../../services/activity-log.service';
import { AuthService } from '../../../services/auth.service';
import { HttpErrorHandlerService } from '../../../services/http-error-handler.service';
import { LookupService } from '../../../services/lookup.service';
import { PopupService } from '../../../services/popup.service';
import { SalesService } from '../../../services/sales.service';
import { StudyMimeType } from '../../enums/study-mime-type.enum';
import { SpinnerService } from '../../services/spinner.service';
import { ToastService } from '../../services/toast.service';
import {
  CancelStudyFiles,
  CancelStudyFilesFailure,
  CancelStudyFilesSuccess,
  CancelStudySubmission,
  CancelStudySubmissionFailure,
  CancelStudySubmissionSuccess,
  CreateStudyFile,
  CreateStudyFileFailure,
  CreateStudyFileSuccess,
  DeleteStudyFile,
  DeleteStudyFileFailure,
  DeleteStudyFileSuccess,
  EndActivity,
  EndActivityFailure,
  EndActivitySuccess,
  GetMediaGroups,
  GetMediaGroupsFailure,
  GetMediaGroupsSuccess,
  GetSaleHorseStudies,
  GetSaleHorseStudiesFailure,
  GetSaleHorseStudiesSuccess,
  GetSaleSettings,
  GetSaleSettingsFailure,
  GetSaleSettingsSuccess,
  GetSharedClinicalData,
  GetSharedClinicalDataFailure,
  GetSharedClinicalDataSuccess,
  GetStatuses,
  GetStatusesFailure,
  GetStatusesSuccess,
  GetStudies,
  GetStudiesFailure,
  GetStudiesManifestFailure,
  GetStudiesManifestSuccess,
  GetStudiesSuccess,
  GetStudyById,
  GetStudyByIdSuccess,
  GetStudyFiles,
  GetStudyFilesBySaleHorse,
  GetStudyFilesBySaleHorseFailure,
  GetStudyFilesBySaleHorseSuccess,
  GetStudyFilesByStudyId,
  GetStudyFilesDocTypes,
  GetStudyFilesDocTypesFailure,
  GetStudyFilesDocTypesSuccess,
  GetStudyFilesFailure,
  GetStudyFilesMimeTypes,
  GetStudyFilesMimeTypesFailure,
  GetStudyFilesMimeTypesSuccess,
  GetStudyFilesSuccess,
  GetStudyFilesWithSaleHorse,
  GetStudyPdfFilesByStudyId,
  GetStudyPdfFilesByStudyIdFailure,
  GetStudyPdfFilesByStudyIdSuccess,
  GetStudyStatus,
  GetStudyStatusFailure,
  GetStudyStatusSuccess,
  mergeStudies,
  mergeStudiesFailure,
  mergeStudiesSuccess,
  OnCompleteBatch,
  OnCompleteBatchFailure,
  OnCompleteBatchSuccess,
  SetStudyActiveStatusFiles,
  SetStudyActiveStatusFilesFailure,
  SetStudyActiveStatusFilesSuccess,
  ShareClinicalData,
  ShareClinicalDataFailure,
  ShareClinicalDataSuccess,
  StartActivity,
  StartActivityFailure,
  StartActivitySuccess,
  StudiesSearch,
  StudiesSearchFailure,
  StudiesSearchSuccess,
  StudySubmitToSale,
  StudySubmitToSaleFailure,
  StudySubmitToSaleSuccess,
  UpdateDescriptionSuccess,
  UpdateDocTypeSuccess,
  UpdateFailure,
  UpdateSelectedTab,
  UpdateSelectedTabSuccess,
  UpdateStudyDescription,
  UpdateStudyFile,
  UpdateStudyFileDocType,
  UpdateStudyFileFailure,
  UpdateStudyFileStatuses,
  UpdateStudyFileStatusesAutoApprove,
  UpdateStudyFileStatusesFailure,
  UpdateStudyFileStatusesSuccess,
  UpdateStudyFileSuccess,
  UpdateStudyStatus,
  UpdateStudyStatusFailure,
  UpdateStudyStatusSuccess,
} from './studies.action';

@Injectable()
export class StudiesEffect {
  constructor(
    private actions$: Actions,
    private studyService: StudyService,
    private authService: AuthService,
    private activityLogService: ActivityLogService,
    private saleService: SalesService,
    private toastService: ToastService,
    private lookupService: LookupService,
    private spinnerService: SpinnerService,
    private httpErrorHandlerService: HttpErrorHandlerService,
    private popupService: PopupService,
  ) {}

  loadAllStudies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetStudies),
      tap(() => this.spinnerService.addRequest('loadAllStudies')),
      mergeMap((action) => {
        if (action.loadManifest) {
          return this.studyService.getStudiesManifest().pipe(
            map((manifest) => {
              this.spinnerService.popRequest('loadAllStudies');
              this.popupService.openManifestViewerPopup(manifest);
              return GetStudiesManifestSuccess({ manifest });
            }),
            catchError((err) => {
              this.spinnerService.popRequest('loadAllStudies');
              this.popupService.openManifestViewerPopup(err);
              return of(GetStudiesManifestFailure({ err }));
            })
          );
        }

        return this.studyService.getStudies().pipe(
          map((studies) => {
            this.spinnerService.popRequest('loadAllStudies');
            return GetStudiesSuccess({ studies });
          }),
          catchError((err) => {
            this.spinnerService.popRequest('loadAllStudies');
            return of(GetStudiesFailure({ err }));
          })
        );
      })
    )
  );

  loadStudy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetStudyById),

      mergeMap((model) => {
        return forkJoin([
          this.studyService.getStudy(model.studyId),
          this.studyService.getStudyStatus(model.studyId),
          // this.studyService.getStudyFiles(model.studyId),
        ]).pipe(
          tap(() => {
            this.spinnerService.addRequest('loadStudy');
            this.spinnerService.addRequest('loadStudyFiles');
          }),
          mergeMap(([study, studyStatus]) => {
            this.spinnerService.popRequest('loadStudy');
            return this.studyService.getStudyFiles(study.studyId).pipe(
              map((studyFiles) => {
                study.isSalesCompany = this.authService.IsSalesCompany();
                this.spinnerService.popRequest('loadStudyFiles');
                return GetStudyByIdSuccess({
                  studyProfile: { ...study },
                  studyFiles,
                  studyStatus,
                });
              }),
              catchError((err) => {
                this.spinnerService.popRequest('loadStudyFiles');
                return of();
              })
            );
          }),
          catchError((err) => {
            this.spinnerService.popRequest('loadStudy');
            return of();
          })
        );
      })
    )
  );

  loadStudyFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetStudyFiles),
      tap(() => this.spinnerService.addRequest('loadStudyFiles')),
      mergeMap((model) => {
        return this.studyService
          .getSaleHorseStudyFiles(model.saleId, model.horseId)
          .pipe(
            map((studyFiles) => {
              this.spinnerService.popRequest('loadStudyFiles');
              const isSalesCompany = this.authService.IsSalesCompany();
              return GetStudyFilesSuccess({
                studyFiles,
                saleId: model.saleId,
                horseId: model.horseId,
                isSalesCompany
              });
            }),
            catchError((err) => {
              this.spinnerService.popRequest('loadStudyFiles');
              return of(GetStudyFilesFailure({ err }));
            })
          );
      })
    )
  );

  mergeStudies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(mergeStudies),
      tap(() => this.spinnerService.addRequest('mergeStudies')),
      mergeMap(({ studyId, studyIds }) =>
        this.studyService.mergeStudies(studyId, studyIds).pipe(
          map((success: boolean) => {
            this.spinnerService.popRequest('mergeStudies');
            return mergeStudiesSuccess({ success });
          }),
          catchError((error) => {
            this.spinnerService.popRequest('mergeStudies');
            return of(mergeStudiesFailure({ error }));
          })
        )
      )
    )
  );
  onCompleteBatch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OnCompleteBatch),
      tap(() => this.spinnerService.addRequest('onCompleteBatch')),
      mergeMap(({ data }) =>
        this.studyService.onCompleteBatch(data).pipe(
          map((success: boolean) => {
            this.spinnerService.popRequest('onCompleteBatch');
            return OnCompleteBatchSuccess({ response: success });
          }),
          catchError((error) => {
            this.spinnerService.popRequest('onCompleteBatch');
            return of(OnCompleteBatchFailure({ error }));
          })
        )
      )
    )
  );

  loadStudyFilesByStudyId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetStudyFilesByStudyId),
      tap(() => this.spinnerService.addRequest('loadStudyFilesByStudyId')),
      mergeMap((model) => {
        return this.studyService.getStudyFiles(model.studyId).pipe(
          map((studyFiles) => {
            this.spinnerService.popRequest('loadStudyFilesByStudyId');
            const isSalesCompany = this.authService.IsSalesCompany();
            return GetStudyFilesSuccess({
              studyFiles,
              saleId: null,
              horseId: null,
              isSalesCompany
            });
          }),
          catchError((err) => {
            this.spinnerService.popRequest('loadStudyFilesByStudyId');
            return of(GetStudyFilesFailure({ err }));
          })
        );
      })
    )
  );

  loadStudyPdfFilesByStudyId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetStudyPdfFilesByStudyId),
      tap(() => this.spinnerService.addRequest('loadStudyPdfFilesByStudyId')),
      mergeMap((model) => {
        return this.studyService
          .getStudyFilesByMimeType(
            model.saleId,
            model.horseId,
            StudyMimeType.XRAY_REPORT
          )
          .pipe(
            map((studyFiles) => {
              this.spinnerService.popRequest('loadStudyPdfFilesByStudyId');
              return GetStudyPdfFilesByStudyIdSuccess({
                studyFiles,
              });
            }),
            catchError((err) => {
              this.spinnerService.popRequest('loadStudyPdfFilesByStudyId');
              return of(GetStudyPdfFilesByStudyIdFailure({ err }));
            })
          );
      })
    )
  );

  loadStudyFilesBySaleHorse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetStudyFilesBySaleHorse),
      tap(() => this.spinnerService.addRequest('loadStudyFilesBySaleHorse')),
      mergeMap((model) => {
        return this.studyService
          .getSaleHorseStudyFiles(
            model.saleHorse.saleId,
            model.saleHorse.horseId
          )
          .pipe(
            map((studyFiles) => {
              const isSalesCompany = this.authService.IsSalesCompany();
              this.spinnerService.popRequest('loadStudyFilesBySaleHorse');
              return GetStudyFilesBySaleHorseSuccess({
                saleHorse: model.saleHorse,
                studyFiles,
                isSalesCompany
              });
            }),
            catchError((err) => {
              this.spinnerService.popRequest('loadStudyFilesBySaleHorse');
              return of(GetStudyFilesBySaleHorseFailure({ err }));
            })
          );
      })
    )
  );

  loadStudyFilesWithSaleHorse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetStudyFilesWithSaleHorse),
      tap(() => this.spinnerService.addRequest('loadStudyFilesWithSaleHorse')),
      mergeMap((model) => {
        return forkJoin([
          this.studyService.getSaleHorseStudyFiles(model.saleId, model.horseId),
          this.saleService.getSaleHorse(model.saleId, model.horseId),
        ]).pipe(
          map((result) => {
            this.spinnerService.popRequest('loadStudyFilesWithSaleHorse');
            const isSalesCompany = this.authService.IsSalesCompany();
            return GetStudyFilesBySaleHorseSuccess({
              saleHorse: result[1],
              studyFiles: result[0],
              isSalesCompany
            });
          }),
          catchError((err) => {
            this.spinnerService.popRequest('loadStudyFilesWithSaleHorse');
            return of(GetStudyFilesBySaleHorseFailure({ err }));
          })
        );
      })
    )
  );

  loadSaleHorseStudies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetSaleHorseStudies),
      //tap(() => this.spinnerService.addRequest('loadSaleHorseStudies')),
      mergeMap((model) => {
        return this.studyService
          .getSaleHorseStudies(model.saleId, model.horseId, model.isHideSpinner)
          .pipe(
            map((saleHorseStudy) => {
              const isSalesCompany = this.authService.IsSalesCompany();
              saleHorseStudy.isSalesCompany = isSalesCompany;
              // this.spinnerService.popRequest('loadSaleHorseStudies');
              return GetSaleHorseStudiesSuccess({ saleHorseStudy });
            }),
            catchError((err) => {
              //this.spinnerService.popRequest('loadSaleHorseStudies');
              return of(GetSaleHorseStudiesFailure(err));
            })
            //finalize(() => this.spinnerService.popRequest('loadSaleHorseStudies'))
          );
      })
    )
  );

  loadStatuses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetStatuses),
      tap(() => this.spinnerService.addRequest('loadStatuses')),
      mergeMap(() => {
        return this.studyService.getStatuses().pipe(
          map((statuses) => {
            this.spinnerService.popRequest('loadStatuses');
            return GetStatusesSuccess({ statuses });
          }),
          catchError((err) => {
            this.spinnerService.popRequest('loadStatuses');
            return of(GetStatusesFailure(err));
          })
        );
      })
    )
  );

  loadStudyStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetStudyStatus),
      tap(() => this.spinnerService.addRequest('loadStudyStatus')),
      mergeMap((model) => {
        return this.studyService.getStudyStatus(model.studyId).pipe(
          map((status) => {
            this.spinnerService.popRequest('loadStudyStatus');
            return GetStudyStatusSuccess({ status });
          }),
          catchError((err) => {
            this.spinnerService.popRequest('loadStudyStatus');
            return of(GetStudyStatusFailure(err));
          })
        );
      })
    )
  );

  loadMediaGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetMediaGroups),
      tap(() => this.spinnerService.addRequest('loadMediaGroups')),
      mergeMap((model) => {
        return this.studyService.getMediaGroups().pipe(
          map((mediaGroups) => {
            this.spinnerService.popRequest('loadMediaGroups');
            return GetMediaGroupsSuccess({ mediaGroups });
          }),
          catchError((err) => {
            this.spinnerService.popRequest('loadMediaGroups');
            return of(GetMediaGroupsFailure(err));
          })
        );
      })
    )
  );

  createStudyFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateStudyFile),
      tap(() => this.spinnerService.addRequest('createStudyFile')),
      mergeMap((model) => {
        return this.studyService
          .createStudyFile(
            model.saleId,
            model.horseId,
            model.file,
            model.studyId,
            model.isAdditional,
            model.mediaCategoryId
          )
          .pipe(
            map((studyFile) => {
              this.spinnerService.popRequest('createStudyFile');
              return CreateStudyFileSuccess({ studyFile });
            }),
            catchError((err) => {
              this.spinnerService.popRequest('createStudyFile');
              return of(CreateStudyFileFailure(err));
            })
          );
      })
    )
  );

  CancelStudyFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CancelStudyFiles),
      tap(() => this.spinnerService.addRequest('cancelStudyFiles')),
      mergeMap((model) => {
        return this.studyService
          .cancelStudyFiles(model.saleId, model.horseId, model.studyId)
          .pipe(
            map((result) => {
              this.spinnerService.popRequest('cancelStudyFiles');
              this.toastService.success(
                'Study and all files have been deleted successfully!',
                'Study'
              );
              return CancelStudyFilesSuccess({ result });
            }),
            catchError((err) => {
              this.spinnerService.popRequest('cancelStudyFiles');
              this.httpErrorHandlerService.ShowErrorMessage(err);
              return of(CancelStudyFilesFailure({ err }));
            })
          );
      })
    )
  );

  CancelStudySubmission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CancelStudySubmission),
      mergeMap((model) => {
        this.spinnerService.addRequest('cancelStudySubmission');
        return this.studyService
          .cancelSubmission(model.saleId, model.horseId)
          .pipe(
            map((result) => {
              this.spinnerService.popRequest('cancelStudySubmission');
              this.toastService.success(
                'Submission has been cancelled successfully!',
                'Study'
              );
              return CancelStudySubmissionSuccess({ result });
            }),
            catchError((err) => {
              this.spinnerService.popRequest('cancelStudySubmission');
              return of(CancelStudySubmissionFailure({ err }));
            })
          );
      })
    )
  );

  SetStudyActiveStatusFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SetStudyActiveStatusFiles),
      tap(() => this.spinnerService.addRequest('SetStudyActiveStatusFiles')),
      mergeMap((model) => {
        return this.studyService
          .setStudyActiveStatusFiles(model.studyId, model.isActive)
          .pipe(
            map((result) => {
              this.spinnerService.popRequest('SetStudyActiveStatusFiles');
              if (model.isActive) {
                this.toastService.success(
                  'Study and all files have been restored successfully!',
                  'Study'
                );
              } else {
                this.toastService.success(
                  'Study and all files have been archived successfully!',
                  'Study'
                );
              }
              return SetStudyActiveStatusFilesSuccess({ result });
            }),
            catchError((err) => {
              this.spinnerService.popRequest('SetStudyActiveStatusFiles');
              return of(SetStudyActiveStatusFilesFailure({ err }));
            })
          );
      })
    )
  );

  updateStudyFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateStudyFile),
      tap(() => this.spinnerService.addRequest('updateStudyFile')),
      mergeMap((model) => {
        if (model.replacing) {
          return this.studyService.replaceStudyFile(model.studyFileId, model.mediaCategoryId, model.studyId, model.saleId, model.horseId).pipe(
            map((studyFile) => {
              this.spinnerService.popRequest('updateStudyFile');
              this.toastService.success(
                'Study file category updated successfully!',
                'Study File'
              );
              return UpdateStudyFileSuccess({ studyFile });
            }),
            catchError((err) => {
              this.spinnerService.popRequest('updateStudyFile');
              return of(UpdateStudyFileFailure(err));
            })
          );
        } else {
          return this.studyService
            .updateStudyFile(
              model.studyFileId,
              model.isAdditional,
              model.mediaCategoryId
            )
            .pipe(
              map((studyFile) => {
                this.spinnerService.popRequest('updateStudyFile');
                this.toastService.success(
                  'Study file category updated successfully!',
                  'Study File'
                );
                return UpdateStudyFileSuccess({ studyFile });
              }),
              catchError((err) => {
                this.spinnerService.popRequest('updateStudyFile');
                return of(UpdateStudyFileFailure(err));
              })
            );
        }
      })
    )
  );

  deleteStudyFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeleteStudyFile),
      tap(() => this.spinnerService.addRequest('deleteStudyFile')),
      mergeMap((model) => {
        return this.studyService.deleteStudyFile(model.studyFileId).pipe(
          map((studyFile) => {
            this.spinnerService.popRequest('deleteStudyFile');
            this.toastService.success(
              'Study file deleted successfully!',
              'Study File'
            );
            return DeleteStudyFileSuccess({
              studyFileId: studyFile.studyFileId,
            });
          }),
          catchError((err) => {
            this.spinnerService.popRequest('deleteStudyFile');
            this.httpErrorHandlerService.ShowErrorMessage(err);
            return of(DeleteStudyFileFailure({ err }));
          })
        );
      })
    )
  );

  updateStudyFileStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateStudyFileStatuses),
      tap(() => this.spinnerService.addRequest('updateStudyFileStatus')),
      mergeMap((model) => {
        return this.studyService
          .updateStudyFileStatuses(
            model.studyFileIds,
            model.statusId,
            model.reason,
            model.notificationData, model.acknowledgementData
          )
          .pipe(
            map((updateStatusResponse) => {
              this.spinnerService.popRequest('updateStudyFileStatus');
              const response = updateStatusResponse as any;
              if ((response?.warnings?.length ?? 0) > 0) {
                this.httpErrorHandlerService.ShowWarning(
                  response.warnings[0],
                  'Warning!'
                );
              } else {
                this.toastService.success(
                  'Study has been successfully updated'
                );
              }
              return UpdateStudyFileStatusesSuccess({
                updateStatusResponse: response.data,
              });
            }),
            catchError((err) => {
              this.spinnerService.popRequest('updateStudyFileStatus');
              this.httpErrorHandlerService.ShowErrorMessage(err);
              return of(UpdateStudyFileStatusesFailure({ err: err }));
            })
          );
      })
    )
  );

  updateStudyFileStatusesAutoApprove$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateStudyFileStatusesAutoApprove),
      tap(() =>
        this.spinnerService.addRequest('updateStudyFileStatusAutoApprove')
      ),
      mergeMap((model) => {
        return this.studyService
          .updateStudyFileStatusesAutoApprove(
            model.studyFileIds,
            model.statusId,
            model.reason,
            model.notificationData,model.acknowledgementData
          )
          .pipe(
            map((updateStatusResponse) => {
              this.spinnerService.popRequest(
                'updateStudyFileStatusAutoApprove'
              );
              const response = updateStatusResponse as any;
              if ((response?.warnings?.length ?? 0) > 0) {
                this.httpErrorHandlerService.ShowWarning(
                  response.warnings[0],
                  'Warning!'
                );
              } else {
                this.toastService.success(
                  'Study has been successfully updated'
                );
              }
              return UpdateStudyFileStatusesSuccess({
                updateStatusResponse: response.data,
              });
            }),
            catchError((err) => {
              this.spinnerService.popRequest(
                'updateStudyFileStatusAutoApprove'
              );
              this.httpErrorHandlerService.ShowErrorMessage(err);
              return of(UpdateStudyFileStatusesFailure({ err: err }));
            })
          );
      })
    )
  );

  updateStudyStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateStudyStatus),
      tap(() => this.spinnerService.addRequest('updateStudyStatus')),
      mergeMap((model) => {
        return this.studyService
          .updateStudyStatus(model.studyId, model.statusId)
          .pipe(
            mergeMap((updateStatusResponse) => {
              return this.studyService
                .updateStudyFileStatuses(
                  model.studyFileIds,
                  model.statusId,
                  '',
                  null,null
                )
                .pipe(
                  map((res) => {
                    this.spinnerService.popRequest('updateStudyFileStatus');
                    const model = new UpdateStudyAndStudyFileStatusResponse();
                    const response = (res as any).data;
                    if (response.warnings.length > 0) {
                      this.httpErrorHandlerService.ShowWarning(
                        response.warnings[0],
                        'Warning!',
                        false
                      );
                    }
                    model.statusId = updateStatusResponse.statusId;
                    model.statusName = updateStatusResponse.statusName;
                    model.studyFileIds = response.studyFileIds;
                    model.studyId = updateStatusResponse.studyId;
                    model.studyFileStatusMessage = response.statusMessage;

                    return UpdateStudyStatusSuccess({
                      updateStatusResponse: model,
                    });
                  })
                );
            }),
            catchError((err) => {
              this.spinnerService.popRequest('updateStudyFileStatus');
              this.httpErrorHandlerService.ShowErrorMessage(err);
              return of(UpdateStudyStatusFailure({ err: err }));
            })
          );
      })
    )
  );

  startActivity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StartActivity),
      mergeMap((model) => {
        return this.activityLogService.addActivityLog(model.activity).pipe(
          map((activityLogId) => {
            return StartActivitySuccess({ activityLogId });
          }),
          catchError((err) => {
            return of(StartActivityFailure(err));
          })
        );
      })
    )
  );

  endActivity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EndActivity),
      mergeMap((model) => {
        return this.activityLogService
          .updateActivityLog(model.activityLogId)
          .pipe(
            map((response) => {
              return EndActivitySuccess({ response });
            }),
            catchError((err) => {
              return of(EndActivityFailure(err));
            })
          );
      })
    )
  );

  loadStudiesByLotNumber$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StudiesSearch),
      tap(() => this.spinnerService.addRequest('loadStudiesByLotNumber')),
      mergeMap((model) => {
        return this.saleService.getHorsesByLotNumber(model.lotNumber).pipe(
          map((response) => {
            this.spinnerService.popRequest('loadStudiesByLotNumber');
            return StudiesSearchSuccess({ response });
          }),
          catchError((err) => {
            this.spinnerService.popRequest('loadStudiesByLotNumber');
            return of(StudiesSearchFailure(err));
          })
        );
      })
    )
  );

  loadStudyFilesMimeTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetStudyFilesMimeTypes),
      tap(() => this.spinnerService.addRequest('loadStudyFilesMimeTypes')),
      mergeMap((model) => {
        return this.lookupService.getStudyFileMimeTypes().pipe(
          map((response) => {
            this.spinnerService.popRequest('loadStudyFilesMimeTypes');
            return GetStudyFilesMimeTypesSuccess({ mimeTypes: response });
          }),
          catchError((err) => {
            this.spinnerService.popRequest('loadStudyFilesMimeTypes');
            return of(GetStudyFilesMimeTypesFailure({ err: err }));
          })
        );
      })
    )
  );

  loadStudyFilesDocTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetStudyFilesDocTypes),
      tap(() => this.spinnerService.addRequest('loadStudyFilesDocTypes')),
      mergeMap((model) => {
        return this.lookupService.getStudySaleDocTypes(model.isReport).pipe(
          map((response) => {
            this.spinnerService.popRequest('loadStudyFilesDocTypes');
            return GetStudyFilesDocTypesSuccess({ docTypes: response });
          }),
          catchError((err) => {
            this.spinnerService.popRequest('loadStudyFilesDocTypes');
            return of(GetStudyFilesDocTypesFailure({ err: err }));
          })
        );
      })
    )
  );

  updateStudyFilesDocTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateStudyFileDocType),
      tap(() => this.spinnerService.addRequest('updateStudyFilesDocTypes')),
      mergeMap((model) => {
        return this.studyService
          .updateStudySaleDocType(
            model.studyFileId,
            model.docTypeId,
            model.isReport
          )
          .pipe(
            map((response) => {
              this.spinnerService.popRequest('updateStudyFilesDocTypes');
              return UpdateDocTypeSuccess({
                studyFileId: model.studyFileId,
                docTypeId: model.docTypeId,
                docTypeName: model.docTypeName,
              });
            }),
            catchError((err) => {
              this.spinnerService.popRequest('updateStudyFilesDocTypes');
              return of(UpdateFailure({ err: err }));
            })
          );
      })
    )
  );

  updateStudyDescription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateStudyDescription),
      tap(() => this.spinnerService.addRequest('updateStudyDescription')),
      mergeMap((model) => {
        return this.studyService
          .updateStudyDescription(model.studyId, model.description)
          .pipe(
            map((response) => {
              this.spinnerService.popRequest('updateStudyDescription');
              return UpdateDescriptionSuccess({ response: response });
            }),
            catchError((err) => {
              this.spinnerService.popRequest('updateStudyDescription');
              return of(UpdateFailure({ err: err }));
            })
          );
      })
    )
  );
  updateSelectedTab$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateSelectedTab),
      map((action) =>
        UpdateSelectedTabSuccess({
          submissionStatusId: action.submissionStatusId,
        })
      )
    )
  );
  submitStudySale$ = createEffect(() =>
    this.actions$.pipe(
      ofType(StudySubmitToSale),
      tap(() => this.spinnerService.addRequest('submitStudySale')),
      mergeMap((model) => {
        return this.studyService.setStudySubmitToSale(model.studySale).pipe(
          map((response) => {
            this.spinnerService.popRequest('submitStudySale');
            return StudySubmitToSaleSuccess({ response });
          }),
          catchError((err) => {
            this.spinnerService.popRequest('submitStudySale');
            return of(StudySubmitToSaleFailure({ err }));
          })
        );
      })
    )
  );

  loadSaleSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetSaleSettings),
      tap(() => this.spinnerService.addRequest('loadSaleSettings')),
      mergeMap((model) => {
        return this.saleService
          .getSaleHorseSettings(model.saleId, model.horseId)
          .pipe(
            map((saleSettings) => {
              this.spinnerService.popRequest('loadSaleSettings');
              return GetSaleSettingsSuccess({ saleSettings });
            }),
            catchError((err) => {
              this.spinnerService.popRequest('loadSaleSettings');
              return of(GetSaleSettingsFailure(err));
            })
          );
      })
    )
  );

  shareClinicalData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ShareClinicalData),
      tap(() => this.spinnerService.addRequest('shareClinicalData')),
      mergeMap((model) => {
        return this.studyService.shareStudy(model.shareStudy).pipe(
          map((success) => {
            this.spinnerService.popRequest('shareClinicalData');
            return ShareClinicalDataSuccess({ success });
          }),
          catchError((err) => {
            this.spinnerService.popRequest('shareClinicalData');
            return of(ShareClinicalDataFailure(err));
          })
        );
      })
    )
  );
  fetchShareClinicalData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetSharedClinicalData),
      tap(() => this.spinnerService.addRequest('fetchShareClinicalData')),
      mergeMap((model) => {
        return this.studyService.sharedStudyData(model.protected).pipe(
          map((studyShare) => {
            this.spinnerService.popRequest('fetchShareClinicalData');
            return GetSharedClinicalDataSuccess({ studyShare });
          }),
          catchError((err) => {
            this.spinnerService.popRequest('fetchShareClinicalData');
            //this.httpErrorHandlerService.ShowErrorMessage(err, "Error!");
            return of(GetSharedClinicalDataFailure({ error: err }));
          })
        );
      })
    )
  );
}
